import React from 'react';
import { Form } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import ErrorIcon from '@mui/icons-material/Error';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// ########################################################
// ### STYLED PUBLIC COMPONENTS
// ########################################################

// Commone styles for the public pages
// That is, the pages that are not behind the login

export const Background = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.custom.navyBlue.main,
  height: '100svh',
}));

export const PageGrid = styled((props) => <Box component="main" {...props} />)(
  ({ theme }) => ({
    display: 'grid',
    gridTemplateAreas: '"left center right"',
    gridTemplateColumns: '16px 1fr 16px',
    alignItems: 'flex-start',
    justifyContent: 'center',

    [theme.breakpoints.up('sm')]: {
      gridTemplateAreas: '"left center right"',
      gridTemplateColumns: '0.25fr 1fr 0.25fr',
    },

    [theme.breakpoints.up('md')]: {
      gridTemplateAreas: '"left center right"',
      gridTemplateColumns: '1fr 1fr 1fr',
    },

    [theme.breakpoints.up('lg')]: {
      gridTemplateAreas: '"left center right"',
      gridTemplateColumns: '1fr auto 1fr',
    },
  })
);

export const PageCenterColumn = styled(Box)(({ theme }) => ({
  gridArea: 'center', // Place this component in the center area
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  margin: '0 auto',
  marginTop: theme.spacing(4),
  gap: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    width: '512px',
  },
}));

export const PageHeader = styled((props) => (
  <Typography variant="h4" component="h1" {...props} />
))(({ theme }) => ({
  color: theme.palette.common.white,
}));

export const FormContainer = styled(CardContent)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

export const FormHeader = styled((props) => (
  <Typography variant="h6" component="h2" {...props} />
))(({ theme }) => ({
  color: theme.palette.text.primary,
}));

export const FormDescription = styled((props) => (
  <Typography variant="body2" component="p" {...props} />
))(({ theme }) => ({
  color: theme.palette.text.primary,
}));

export const CardForm = styled((props) => <Card component={Form} {...props} />)(
  ({ _theme }) => ({
    width: '100%',
  })
);

export const CardFooter = styled(CardActions)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  padding: theme.spacing(2),
}));

export const LogoContainer = styled(Box)(({ theme }) => ({
  width: '80%',
  marginTop: theme.spacing(4),
}));

// ########################################################
// ### STYLED FUNCTIONAL COMPONENTS
// ########################################################

export function ErrorMessage({ error }) {
  return (
    <Stack direction="row" alignItems="flex-start" gap={1} sx={{ mb: 2 }}>
      <ErrorIconWrapper />
      <ErrorMessageText>{error}</ErrorMessageText>
    </Stack>
  );
}

ErrorMessage.propTypes = {
  error: PropTypes.string,
};

// ########################################################
// ### PRIVATE STYLED COMPONENTS
// ########################################################

const ErrorMessageText = styled((props) => (
  <Typography variant="body2" component="p" {...props} />
))(({ theme }) => ({
  color: theme.palette.error.main,
  fontWeight: theme.typography.fontWeightMedium,
}));

const ErrorIconWrapper = styled((props) => <ErrorIcon {...props} />)(
  ({ theme }) => ({
    color: theme.palette.error.main,
  })
);
